import React from 'react'
import { Section } from '../components/Section'
import { THICK_CATEGORIES, THIN_CATEGORIES } from '../utils/spanHighlight'

type TaxonomyRow = {
  category: string
  type: 'thin' | 'thick'
  description: string
  example: string
}

const TAXONOMY_GROUPS: { group: string; rows: TaxonomyRow[] }[] = [
  {
    group: 'Linguistic Errors',
    rows: [
      {
        category: 'Explicit Linguistic Error',
        type: 'thin',
        description: 'Grammatical mistakes, spelling errors, and other rule-based surface violations.',
        example: 'Dangling modifiers, comma splices, incorrect verb tense.',
      },
      {
        category: 'Implicit Linguistic Error',
        type: 'thick',
        description: 'Failures of tone, register, or sociolinguistic appropriateness that are not rule-based.',
        example: 'Using British "queue" in an American-English context.',
      },
    ],
  },
  {
    group: 'Cultural Errors',
    rows: [
      {
        category: 'Cultural Inaccuracy',
        type: 'thin',
        description: 'Objectively false, verifiable factual claims about a culture.',
        example: 'A wrong historical date, a non-existent landmark, a misattributed tradition.',
      },
      {
        category: 'Cultural Incoherence',
        type: 'thick',
        description: 'Culturally incongruous elements that clash within a specific local context.',
        example: 'A martabak vendor during an Indonesian morning commute (martabak is an evening food).',
      },
      {
        category: 'Cultural Specificity Error',
        type: 'thick',
        description: 'Over-generalizing or misrepresenting the boundaries of a cultural concept.',
        example: 'Using "outer boroughs" to describe a generic American city (a New York-specific term).',
      },
      {
        category: 'Cultural Connotation Error',
        type: 'thick',
        description: 'Mishandling the symbolic or emotional associations of a cultural element.',
        example: 'Describing jasmine as a symbol of peace in Indonesia, where it connotes funerals.',
      },
      {
        category: 'Cultural Missingness',
        type: 'thick',
        description: 'Absent critical cultural grounding that a local would expect to see.',
        example: 'A story set in Bangladesh that omits the cultural role of tea stalls.',
      },
    ],
  },
]

type CountryRow = {
  country: string
  rowspan: number
  language: string
  pairs: number
  spans: number
  thickRatio: number
}

const COUNTRIES: CountryRow[] = [
  { country: 'United States',  rowspan: 1, language: 'English',    pairs: 150, spans: 661,   thickRatio: 50.2 },
  { country: 'South Korea',    rowspan: 2, language: 'Korean',     pairs: 150, spans: 1107,  thickRatio: 41.7 },
  { country: '',               rowspan: 0, language: 'English',    pairs: 150, spans: 1009,  thickRatio: 40.2 },
  { country: 'Indonesia',      rowspan: 2, language: 'Indonesian', pairs: 150, spans: 1237,  thickRatio: 38.3 },
  { country: '',               rowspan: 0, language: 'English',    pairs: 150, spans: 907,   thickRatio: 45.1 },
  { country: 'Bangladesh',     rowspan: 2, language: 'Bengali',    pairs: 150, spans: 1734,  thickRatio: 35.6 },
  { country: '',               rowspan: 0, language: 'English',    pairs: 150, spans: 815,   thickRatio: 40.2 },
]

export const Dataset = () => {
  return (
    <Section title="Dataset">
      <p className="text-gray-700 leading-relaxed mb-8">
        JuICE covers <strong>1,050 query-response pairs</strong> generated by frontier LLMs across four
        countries and seven language combinations. Each response is annotated with span-level labels by two
        native-speaker annotators and two LLM annotators in parallel, then cross-validated to produce a
        conservative consensus set of <strong>7,470 annotations</strong>. Queries span three task types:
        story generation, creative ideation, and how-to advice.
      </p>

      {/* Coverage table */}
      <div className="mb-10">
        <h3 className="text-base font-semibold text-gray-800 mb-1">Geographic & Linguistic Coverage</h3>
        <p className="text-sm text-gray-500 mb-3">
          US responses are English-only; all other countries include both the local language and English.
          Thick Ratio is the proportion of annotations labeled as thick errors — culturally nuanced errors
          that require interpretive, situated judgment rather than surface-level verification.
        </p>
        <div className="max-w-xl mx-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-white border-b-2 border-gray-300">
                <th className="text-center py-2 px-3 font-bold text-gray-900">Country</th>
                <th className="text-center py-2 px-3 font-bold text-gray-900">Language</th>
                <th className="text-center py-2 px-3 font-bold text-gray-900">Query-response Pairs</th>
                <th className="text-center py-2 px-3 font-bold text-gray-900">Error Spans</th>
                <th className="text-center py-2 px-3 font-bold text-gray-900">Thick Ratio</th>
              </tr>
            </thead>
            <tbody>
              {COUNTRIES.map((c, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {c.rowspan > 0 && (
                    <td rowSpan={c.rowspan} className="py-2 px-3 text-gray-700 font-medium align-middle border-r border-gray-100">
                      {c.country}
                    </td>
                  )}
                  <td className="py-2 px-3 text-gray-600 text-center">{c.language}</td>
                  <td className="py-2 px-3 text-gray-600 text-center">{c.pairs}</td>
                  <td className="py-2 px-3 text-gray-600 text-center">{c.spans.toLocaleString()}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`text-xs font-semibold ${c.thickRatio >= 45 ? 'text-teal-600' : 'text-gray-500'}`}>
                      {c.thickRatio}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Taxonomy table */}
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Error Taxonomy</h3>
        <p className="text-sm text-gray-500 mb-3">
          JuICE operationalizes a <em>thick vs. thin</em> distinction. Thin categories involve
          surface-level, verifiable errors; thick categories require interpretive, culturally situated judgment.
        </p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-gray-300">
              <th className="text-center py-2 px-3 font-bold text-gray-900 w-[200px]">Category</th>
              <th className="text-center py-2 px-3 font-bold text-gray-900 w-14">Type</th>
              <th className="text-center py-2 px-3 font-bold text-gray-900">Description</th>
              <th className="text-center py-2 px-3 font-bold text-gray-900 hidden md:table-cell">Example</th>
            </tr>
          </thead>
          <tbody>
            {TAXONOMY_GROUPS.map((group, gi) => (
              <React.Fragment key={group.group}>
                <tr className={gi > 0 ? 'border-t-2 border-gray-200' : ''}>
                  <td
                    colSpan={4}
                    className="py-1.5 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50"
                  >
                    {group.group}
                  </td>
                </tr>
                {group.rows.map(row => (
                  <tr key={row.category} className="border-b border-gray-100 align-top">
                    <td className="py-2.5 px-3 text-gray-700 font-medium">{row.category}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`text-xs font-semibold ${row.type === 'thin' ? 'text-gray-400' : 'text-teal-600'}`}>
                        {row.type === 'thin' ? 'Thin' : 'Thick'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-600 leading-relaxed">{row.description}</td>
                    <td className="py-2.5 px-3 text-gray-500 leading-relaxed hidden md:table-cell">{row.example}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  )
}
